import type { SelectedFilters } from '@/types/filter';
import { FILTER_GROUP } from '@/types/filter';
import type { Game } from '@/types/games';
import { getText } from '@/utils/text/text';
import { STUDIO_FILTERS } from '@/utils/constants';
import { getGameFilters, setGameFilters } from '@/logic/game-adapter/game-adapter';

export const hasGameFilters = (game: Game, filters: SelectedFilters) => {
    const themes = filters[FILTER_GROUP.THEME]?.reduce((acc: any, curr) => acc.concat(curr.uids), []);
    const features = filters[FILTER_GROUP.FEATURE]?.reduce((acc: any, curr) => acc.concat(curr.uids), []);
    const genres = filters[FILTER_GROUP.GENRE]?.reduce((acc: any, curr) => acc.concat(curr.uids), []);
    const studios = filters[FILTER_GROUP.STUDIO]?.reduce((acc: any, curr) => acc.concat(curr.uids), []);

    const gameFilters = getGameFilters(game);

    return (
        // Check themes
        (!themes?.length || gameFilters?.themes?.some((t) => themes.includes(t))) &&
        // Check features
        (!features?.length || gameFilters?.features?.some((f) => features.includes(f))) &&
        // Check genres
        (!genres?.length || gameFilters?.genres?.some((t) => genres.includes(t))) &&
        // Check studio
        (!studios?.length || studios?.includes(gameFilters?.studio))
    );
};

const getTitleByUID = (uid: string) =>
    uid
        .replace(/^[^-]+-/, '') // Remove first prefix that indicates the category
        .replace(/-/g, ' ') // Replace remaining hyphens wth spaces
        .replace(/^(\w)/, (c) => c.toUpperCase()); // Capitalize first letter

const formatFilters = (data: string[]) =>
    data
        .filter(Boolean)
        .map((uid) => ({ title: getTitleByUID(uid), uids: [uid] }))
        .sort((a, b) => a.title.localeCompare(b.title));

const formatStudios = (data: string[]) =>
    STUDIO_FILTERS.filter((item) => item.uids.some((uid: any) => data.includes(uid))).sort((a, b) =>
        a.title.localeCompare(b.title)
    );

export const getAvailableGamesFilters = (games: Game[]): any => {
    const themes = new Set<string>();
    const features = new Set<string>();
    const genres = new Set<string>();
    const studios = new Set<string>();

    games.forEach((game: Game) => {
        setGameFilters(game, themes, features, genres, studios);
    });

    return [
        {
            title: getText({ text: 'components.filterCategory.theme' }),
            icon: 'theme',
            key: FILTER_GROUP.THEME,
            options: formatFilters(Array.from(themes))
        },
        {
            title: getText({ text: 'components.filterCategory.feature' }),
            icon: 'slots',
            key: FILTER_GROUP.FEATURE,
            options: formatFilters(Array.from(features))
        },
        {
            title: getText({ text: 'components.filterCategory.genre' }),
            icon: 'genre',
            key: FILTER_GROUP.GENRE,
            options: formatFilters(Array.from(genres))
        },
        {
            title: getText({ text: 'components.filterCategory.studio' }),
            icon: 'studio',
            key: FILTER_GROUP.STUDIO,
            options: formatStudios(Array.from(studios).filter(Boolean))
        }
        // TODO, remove after business logic approval and change type from any to FilterGroup;
    ].filter(({ key }) => key !== FILTER_GROUP.STUDIO);
};

export const countFilters = (filters: SelectedFilters) =>
    Object.keys(filters).reduce((acc, group) => acc + filters[group as keyof SelectedFilters].length, 0);

export const getFilteredGamesCount = (games: any, filters: SelectedFilters): number =>
    games.reduce((count: number, game: Game) => count + (hasGameFilters(game, filters) ? 1 : 0), 0);

export const getFilteredGames = (games: Game[], filters: SelectedFilters) => {
    return games.filter((game: Game) => hasGameFilters(game, filters));
};
